import React, { useEffect, useState, useRef, useMemo } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Card from "components/card";
import Pagination from "components/pagination/Pagination";
import Loader from "../../assets/img/loader.gif";
import GamificationService from "services/gamification.service";
import tabCache from "utils/tabCache";
import Select from "react-select";
import { AiFillEdit } from "react-icons/ai";
import { IoMdClose } from "react-icons/io";
import { IoEye } from "react-icons/io5";
import { MdCalendarToday, MdChevronLeft, MdChevronRight, MdClose } from "react-icons/md";
import EventService from "services/events.service";
import Toast from "components/toaster";

const BADGE_TYPE_OPTIONS = [
  { value: "level", label: "Level Badges" },
  { value: "achievement", label: "Achievement Badges" },
  { value: "feature", label: "Feature Unlocks" },
  { value: "how_to_earn_xp", label: "How to earn XP" },
  { value: "daily_tasks", label: "Daily Task" },
];

/** Fetch limit for daily tasks list so we get all dates and paginate in the UI (10 per page). */
const DAILY_TASKS_LIST_FETCH_LIMIT = 1000;

const DAILY_TASK_ACTIONS = [
  { value: "EVENT_CREATE", label: "EVENT_CREATE" },
  { value: "EVENT_LIKE", label: "EVENT_LIKE" },
  { value: "EVENT_COMMENT", label: "EVENT_COMMENT" },
  { value: "EVENT_SHARE", label: "EVENT_SHARE" },
  { value: "EVENT_BOOST", label: "EVENT_BOOST" },
  { value: "EVENT_TICKET_BUY", label: "EVENT_TICKET_BUY" },
  { value: "POST_CREATE", label: "POST_CREATE" },
  { value: "POST_LIKE", label: "POST_LIKE" },
  { value: "POST_COMMENT", label: "POST_COMMENT" },
  { value: "POST_SHARE", label: "POST_SHARE" },
  { value: "STORY_CREATE", label: "STORY_CREATE" },
  { value: "STORY_LIKE", label: "STORY_LIKE" },
  { value: "STORY_COMMENT", label: "STORY_COMMENT" },
  { value: "STORY_SHARE", label: "STORY_SHARE" },
  { value: "REEL_CREATE", label: "REEL_CREATE" },
  { value: "REEL_LIKE", label: "REEL_LIKE" },
  { value: "REEL_COMMENT", label: "REEL_COMMENT" },
  { value: "REEL_SHARE", label: "REEL_SHARE" },
  { value: "VENDOR_BOOST", label: "VENDOR_BOOST" },
  { value: "VENDOR_ADD_PRODUCT", label: "VENDOR_ADD_PRODUCT" },
  { value: "VENDOR_FOLLOW", label: "VENDOR_FOLLOW" },
  { value: "GARAGE_ADD", label: "GARAGE_ADD" },
  { value: "GARAGE_CREATE_CHALLENGE", label: "GARAGE_CREATE_CHALLENGE" },
  { value: "GARAGE_ADD_VOTE", label: "GARAGE_ADD_VOTE" },
  { value: "CHAT_CREATE_NEW", label: "CHAT_CREATE_NEW" },
  { value: "CHAT_CREATE_GROUP", label: "CHAT_CREATE_GROUP" },
  { value: "LOGIN", label: "LOGIN" },
];

const DetailRow = ({ label, value, isLong }) => (
  <div>
    <span className="block text-sm font-medium text-gray-400 mb-0.5">{label}</span>
    <span className={`text-white ${isLong ? "text-sm block break-words" : "text-sm"}`}>
      {value ?? "--"}
    </span>
  </div>
);

const colourStyles = {
  control: (styles, { isFocused }) => ({
    ...styles,
    backgroundColor: "rgb(12 14 28 / 1)",
    borderRadius: "8px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    boxShadow: "none",
    minHeight: "40px",
    "&:hover": { borderColor: "rgba(255, 255, 255, 0.2)" },
    ...(isFocused ? { borderColor: "rgba(255, 255, 255, 0.25)" } : {}),
  }),
  singleValue: (styles) => ({ ...styles, color: "#ffffff" }),
  option: (styles) => ({
    ...styles,
    backgroundColor: "rgb(30 30 32 / 1) !important",
    ":hover": { backgroundColor: "rgba(255, 255, 255, 0.15) !important", color: "#ffffff" },
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: "rgb(12 14 28 / 1)",
    borderRadius: "12px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
  }),
};

const SCHEDULE_DATE_MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

const getDaysInMonthForSchedule = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();
  const days = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    const prevMonth = new Date(year, month, 0);
    days.push({
      day: prevMonth.getDate() - startingDayOfWeek + i + 1,
      isCurrentMonth: false,
      date: new Date(year, month - 1, prevMonth.getDate() - startingDayOfWeek + i + 1),
    });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    days.push({ day, isCurrentMonth: true, date: new Date(year, month, day) });
  }
  const remainingCells = 42 - days.length;
  for (let day = 1; day <= remainingCells; day++) {
    days.push({ day, isCurrentMonth: false, date: new Date(year, month + 1, day) });
  }
  return days;
};

const Gamification = () => {
  const [badges, setBadges] = useState(tabCache.gamificationBadges.data || []);
  const [isLoading, setIsLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(tabCache.gamificationBadges.firstLoad);

  const [achievementBadges, setAchievementBadges] = useState(
    tabCache.gamificationAchievementBadges.data || []
  );
  const [achievementLoading, setAchievementLoading] = useState(false);
  const [achievementFirstLoad, setAchievementFirstLoad] = useState(
    tabCache.gamificationAchievementBadges.firstLoad
  );

  const [featureUnlocks, setFeatureUnlocks] = useState(
    tabCache.gamificationFeatureUnlocks.data || []
  );
  const [featureLoading, setFeatureLoading] = useState(false);
  const [featureFirstLoad, setFeatureFirstLoad] = useState(
    tabCache.gamificationFeatureUnlocks.firstLoad
  );

  const [dailyTasks, setDailyTasks] = useState(
    tabCache.gamificationDailyTasks.data || []
  );
  const [dailyTasksLoading, setDailyTasksLoading] = useState(false);
  const [dailyTasksFirstLoad, setDailyTasksFirstLoad] = useState(
    tabCache.gamificationDailyTasks.firstLoad
  );

  const [todayUserTasksData, setTodayUserTasksData] = useState({
    today: "",
    dates: [],
  });
  const [todayUserTasksLoading, setTodayUserTasksLoading] = useState(false);
  const [todayUserTasksPage, setTodayUserTasksPage] = useState(1);
  const [todayUserTasksPageSize, setTodayUserTasksPageSize] = useState(10);
  const [todayUserTasksTotal, setTodayUserTasksTotal] = useState(null); // total count of dates/items from API
  const [todayUserTasksTotalPages, setTodayUserTasksTotalPages] = useState(null); // optional from API

  const [activeType, setActiveType] = useState("level"); // 'level' | 'achievement' | 'feature' | 'how_to_earn_xp' | 'daily_tasks'
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [editType, setEditType] = useState("level"); // 'level' | 'achievement' | 'feature' | 'daily_tasks'
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    xp_required: "",
    badge_url: "",
    is_active: true,
    threshold: "",
    prompt_to_achieve: "",
    xp_reward: "",
    icon: "",
    action: "",
    morning_push_title: "",
    morning_push_header: "",
    afternoon_push_title: "",
    afternoon_push_header: "",
    evening_push_title: "",
    evening_push_header: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [viewType, setViewType] = useState("level");
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [showScheduleDatePicker, setShowScheduleDatePicker] = useState(false);
  const [currentScheduleCalendarMonth, setCurrentScheduleCalendarMonth] = useState(() => new Date());
  const scheduleDatePickerRef = useRef(null);
  const [scheduleForm, setScheduleForm] = useState({
    show_date: "",
    tasks: [
      {
        action: "",
        morning_push_title: "Morning Task",
        morning_push_header: "",
        afternoon_push_title: "Afternoon Task",
        afternoon_push_header: "",
        evening_push_title: "Evening Task",
        evening_push_header: "",
      },
    ],
  });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState("");
  const [editingScheduleDate, setEditingScheduleDate] = useState(null);
  const [editingOriginalAction, setEditingOriginalAction] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [popupOpenDelete, setPopupOpenDelete] = useState(false);
  const [confirmLoader, setConfirmLoader] = useState(false);
  const [badgeToDelete, setBadgeToDelete] = useState(null);
  const deleteRef = useRef(null);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [reorderList, setReorderList] = useState([]);
  const [reorderType, setReorderType] = useState("level"); // 'level' | 'achievement' | 'feature'
  const reorderModalRef = useRef(null);
  const [uploadDailyTaskLoading, setUploadDailyTaskLoading] = useState(false);
  const [uploadDailyTaskError, setUploadDailyTaskError] = useState("");
  const [uploadDailyTaskModalOpen, setUploadDailyTaskModalOpen] = useState(false);
  const dailyTaskFileInputRef = useRef(null);
  const visitedGamificationTypesRef = useRef(new Set(["level"]));

  const fetchBadges = async (forceLoader = false) => {
    const isFirstLoad = tabCache.gamificationBadges.firstLoad;
    const shouldShowLoader = isFirstLoad || forceLoader;

    if (shouldShowLoader) {
      setIsLoading(true);
    }

    try {
      const response = await GamificationService.getBadges();
      const data = response?.data?.data;
      const badgesData = Array.isArray(data) ? data : [];

      setBadges(badgesData);
      tabCache.gamificationBadges.data = badgesData;

      if (isFirstLoad) {
        tabCache.gamificationBadges.firstLoad = false;
        setFirstLoad(false);
      }
    } catch (error) {
      console.error("Error fetching badges:", error);
      const emptyData = [];
      setBadges(emptyData);
      tabCache.gamificationBadges.data = emptyData;
      if (isFirstLoad) {
        tabCache.gamificationBadges.firstLoad = false;
        setFirstLoad(false);
      }
    } finally {
      if (shouldShowLoader) {
        setIsLoading(false);
      }
    }
  };

  const fetchAchievementBadges = async (forceLoader = false) => {
    const isFirstLoad = tabCache.gamificationAchievementBadges.firstLoad;
    const shouldShowLoader = isFirstLoad || forceLoader;

    if (shouldShowLoader) {
      setAchievementLoading(true);
    }

    try {
      const response = await GamificationService.getAchievementBadges();
      const data = response?.data?.data;
      const badgesData = Array.isArray(data) ? data : [];

      setAchievementBadges(badgesData);
      tabCache.gamificationAchievementBadges.data = badgesData;

      if (isFirstLoad) {
        tabCache.gamificationAchievementBadges.firstLoad = false;
        setAchievementFirstLoad(false);
      }
    } catch (error) {
      console.error("Error fetching achievement badges:", error);
      const emptyData = [];
      setAchievementBadges(emptyData);
      tabCache.gamificationAchievementBadges.data = emptyData;
      if (isFirstLoad) {
        tabCache.gamificationAchievementBadges.firstLoad = false;
        setAchievementFirstLoad(false);
      }
    } finally {
      if (shouldShowLoader) {
        setAchievementLoading(false);
      }
    }
  };

  const fetchFeatureUnlocks = async (forceLoader = false) => {
    const isFirstLoad = tabCache.gamificationFeatureUnlocks.firstLoad;
    const shouldShowLoader = isFirstLoad || forceLoader;

    if (shouldShowLoader) {
      setFeatureLoading(true);
    }

    try {
      const response = await GamificationService.getFeatureUnlocks();
      const data = response?.data?.data;
      const list = Array.isArray(data) ? data : [];

      setFeatureUnlocks(list);
      tabCache.gamificationFeatureUnlocks.data = list;

      if (isFirstLoad) {
        tabCache.gamificationFeatureUnlocks.firstLoad = false;
        setFeatureFirstLoad(false);
      }
    } catch (error) {
      console.error("Error fetching feature unlocks:", error);
      const emptyData = [];
      setFeatureUnlocks(emptyData);
      tabCache.gamificationFeatureUnlocks.data = emptyData;
      if (isFirstLoad) {
        tabCache.gamificationFeatureUnlocks.firstLoad = false;
        setFeatureFirstLoad(false);
      }
    } finally {
      if (shouldShowLoader) {
        setFeatureLoading(false);
      }
    }
  };

  const fetchDailyTasks = async (forceLoader = false) => {
    const isFirstLoad = tabCache.gamificationDailyTasks.firstLoad;
    const shouldShowLoader = isFirstLoad || forceLoader;
    if (shouldShowLoader) setDailyTasksLoading(true);
    try {
      const response = await GamificationService.getDailyTasks();
      const data = response?.data?.data;
      const list = Array.isArray(data) ? data : [];
      setDailyTasks(list);
      tabCache.gamificationDailyTasks.data = list;
      if (isFirstLoad) {
        tabCache.gamificationDailyTasks.firstLoad = false;
        setDailyTasksFirstLoad(false);
      }
    } catch (error) {
      console.error("Error fetching daily tasks:", error);
      const emptyData = [];
      setDailyTasks(emptyData);
      tabCache.gamificationDailyTasks.data = emptyData;
      if (isFirstLoad) {
        tabCache.gamificationDailyTasks.firstLoad = false;
        setDailyTasksFirstLoad(false);
      }
    } finally {
      if (shouldShowLoader) setDailyTasksLoading(false);
    }
  };

  const fetchTodayUserTasks = async (page = 1, pageSize = DAILY_TASKS_LIST_FETCH_LIMIT) => {
    setTodayUserTasksLoading(true);
    try {
      const response = await GamificationService.getTodayUserTasks({ page, limit: pageSize });
      const data = response?.data?.data;
      setTodayUserTasksData({
        today: data?.today ?? "",
        dates: Array.isArray(data?.dates) ? data.dates : [],
      });
      if (data?.total != null) setTodayUserTasksTotal(data.total);
      if (data?.totalPages != null) setTodayUserTasksTotalPages(data.totalPages);
    } catch (error) {
      console.error("Error fetching today user tasks:", error);
      setTodayUserTasksData({ today: "", dates: [] });
    } finally {
      setTodayUserTasksLoading(false);
    }
  };

  const handleOpenView = (item, type) => {
    setViewItem(item);
    setViewType(type);
    setViewModalOpen(true);
  };

  const handleOpenEdit = (badge, type, scheduleDate = null) => {
    setSelectedBadge(badge);
    setEditType(type);
    if (type === "daily_tasks") {
      setEditingScheduleDate(scheduleDate ?? null);
      const task = badge.task || badge;
      setEditingOriginalAction(scheduleDate ? (task.action ?? null) : null);
    } else {
      setEditingScheduleDate(null);
      setEditingOriginalAction(null);
    }

    if (type === "level") {
      setEditForm({
        title: badge.title || "",
        description: badge.description || "",
        xp_required:
          typeof badge.xp_required === "number"
            ? String(badge.xp_required)
            : badge.xp_required || "",
        badge_url: badge.badge_url || "",
        is_active: true,
      });
    } else if (type === "feature") {
      setEditForm({
        title: badge.title || "",
        description: badge.description || "",
        xp_required: "",
        badge_url: badge.badge_url || "",
        is_active: badge.is_active !== false,
      });
    } else if (type === "how_to_earn_xp") {
      setEditForm({
        title: badge.title || "",
        description: badge.description || "",
        xp_required: "",
        badge_url: "",
        is_active: true,
        threshold: "",
        prompt_to_achieve: "",
        xp_reward:
          typeof badge.xp_reward === "number"
            ? String(badge.xp_reward)
            : badge.xp_reward != null && badge.xp_reward !== ""
              ? String(badge.xp_reward)
              : "",
        icon: badge.icon ?? "",
        action: "",
        morning_push_title: "",
        morning_push_header: "",
        afternoon_push_title: "",
        afternoon_push_header: "",
        evening_push_title: "",
        evening_push_header: "",
      });
    } else if (type === "daily_tasks") {
      const entry = badge.task ? badge : null;
      const task = badge.task || badge;
      setEditForm({
        title: task.title || "",
        description: "",
        xp_required: "",
        badge_url: "",
        is_active: true,
        threshold: "",
        prompt_to_achieve: "",
        xp_reward: "",
        action: task.action ?? "",
        icon: "",
        morning_push_title: entry?.morning_push_title ?? "",
        morning_push_header: entry?.morning_push_header ?? "",
        afternoon_push_title: entry?.afternoon_push_title ?? "",
        afternoon_push_header: entry?.afternoon_push_header ?? "",
        evening_push_title: entry?.evening_push_title ?? "",
        evening_push_header: entry?.evening_push_header ?? "",
      });
    } else {
      // achievement badge
      setEditForm({
        title: badge.name || "",
        description: badge.description || "",
        xp_required: "",
        badge_url: badge.image_url || "",
        is_active: true,
        threshold:
          typeof badge.threshold === "number"
            ? String(badge.threshold)
            : badge.threshold != null && badge.threshold !== ""
              ? String(badge.threshold)
              : "",
      });
    }
    setEditError("");
    setEditLoading(false);
    setEditModalOpen(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUpdateBadge = async (e) => {
    e.preventDefault();
    if (!selectedBadge) return;

    if (editType !== "daily_tasks" && !editForm.title.trim()) {
      setEditError("Title is required.");
      return;
    }

    setEditLoading(true);
    setEditError("");

    try {
      if (editType === "level") {
        const xpValue = editForm.xp_required !== "" ? Number(editForm.xp_required) : null;
        if (editForm.xp_required !== "" && (Number.isNaN(xpValue) || xpValue < 0)) {
          setEditError("XP Required must be a non-negative number.");
          setEditLoading(false);
          return;
        }

        const payload = {
          title: editForm.title.trim(),
          description: editForm.description.trim(),
          ...(xpValue !== null && { xp_required: xpValue }),
          ...(editForm.badge_url && { badge_url: editForm.badge_url }),
        };

        await GamificationService.updateBadge(selectedBadge._id, payload);
        await fetchBadges();
      } else if (editType === "feature") {
        const payload = {
          title: editForm.title.trim(),
          description: editForm.description.trim(),
          is_active: editForm.is_active,
        };
        if (editForm.badge_url) payload.badge_url = editForm.badge_url;
        await GamificationService.updateFeatureUnlock(selectedBadge._id, payload);
        await fetchFeatureUnlocks();
      } else if (editType === "how_to_earn_xp") {
        const payload = {
          icon: editForm.icon?.trim() ?? "",
          title: editForm.title.trim(),
          description: editForm.description?.trim() ?? "",
          xp_reward: editForm.xp_reward !== "" ? Number(editForm.xp_reward) : null,
        };
        if (editForm.xp_reward !== "" && (Number.isNaN(payload.xp_reward) || payload.xp_reward < 0)) {
          setEditError("XP reward must be a non-negative number.");
          setEditLoading(false);
          return;
        }
        await GamificationService.updateDailyTask(selectedBadge._id, payload);
        await fetchDailyTasks();
      } else if (editType === "daily_tasks") {
        if (editingScheduleDate) {
          const payload = {
            show_date: editingScheduleDate,
            action: editingOriginalAction ?? editForm.action?.trim() ?? "",
            new_action: editForm.action?.trim() ?? "",
            morning_push_header: editForm.morning_push_header?.trim() ?? "",
            afternoon_push_header: editForm.afternoon_push_header?.trim() ?? "",
            evening_push_header: editForm.evening_push_header?.trim() ?? "",
            morning_push_title: editForm.morning_push_title?.trim() ?? "",
            afternoon_push_title: editForm.afternoon_push_title?.trim() ?? "",
            evening_push_title: editForm.evening_push_title?.trim() ?? "",
          };
          await GamificationService.updateDailyTaskSchedule(payload);
        } else {
          const taskId = selectedBadge.task?._id || selectedBadge._id;
          const payload = {
            action: editForm.action?.trim() ?? "",
            morning_push_title: editForm.morning_push_title?.trim() ?? "",
            morning_push_header: editForm.morning_push_header?.trim() ?? "",
            afternoon_push_title: editForm.afternoon_push_title?.trim() ?? "",
            afternoon_push_header: editForm.afternoon_push_header?.trim() ?? "",
            evening_push_title: editForm.evening_push_title?.trim() ?? "",
            evening_push_header: editForm.evening_push_header?.trim() ?? "",
          };
          await GamificationService.updateDailyTask(taskId, payload);
        }
        await fetchDailyTasks();
        await fetchTodayUserTasks();
      } else {
        // achievement badge: update name, description, threshold, image
        const thresholdNum =
          editForm.threshold !== "" ? Number(editForm.threshold) : null;
        if (
          editForm.threshold !== "" &&
          (Number.isNaN(thresholdNum) || thresholdNum < 0)
        ) {
          setEditError("Threshold must be a non-negative number.");
          setEditLoading(false);
          return;
        }
        const payload = {
          name: editForm.title.trim(),
          description: editForm.description.trim(),
          ...(thresholdNum !== null && { threshold: thresholdNum }),
          ...(editForm.badge_url && { image_url: editForm.badge_url }),
        };
        await GamificationService.updateAchievementBadge(selectedBadge._id, payload);
        await fetchAchievementBadges();
      }

      setEditModalOpen(false);
      setSelectedBadge(null);
      setEditingScheduleDate(null);
      setEditingOriginalAction(null);
      setEditForm({ title: "", description: "", xp_required: "", badge_url: "", is_active: true, threshold: "", prompt_to_achieve: "", xp_reward: "", icon: "", action: "", morning_push_title: "", morning_push_header: "", afternoon_push_title: "", afternoon_push_header: "", evening_push_title: "", evening_push_header: "" });
    } catch (error) {
      console.error("Error updating badge:", error);
      setEditError(
        error?.response?.data?.message || "Failed to update badge. Please try again."
      );
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteBadge = (badge, type) => {
    setBadgeToDelete({ ...badge, _type: type });
    setPopupOpenDelete(true);
    setDeleteError("");
    document.body.style.overflow = "hidden";
  };

  const confirmDeleteBadge = async () => {
    if (!badgeToDelete) return;

    setConfirmLoader(true);
    setDeleteError("");
    try {
      if (badgeToDelete._type === "achievement") {
        await GamificationService.deleteAchievementBadge(badgeToDelete._id);
        await fetchAchievementBadges();
      } else if (badgeToDelete._type === "feature") {
        await GamificationService.deleteFeatureUnlock(badgeToDelete._id);
        await fetchFeatureUnlocks();
      } else {
        await GamificationService.deleteBadge(badgeToDelete._id);
        await fetchBadges();
      }
      setPopupOpenDelete(false);
      setBadgeToDelete(null);
      document.body.style.overflow = "unset";
    } catch (error) {
      console.error("Error deleting badge:", error);
      setDeleteError(
        error?.response?.data?.message || "Failed to delete badge. Please try again."
      );
    } finally {
      setConfirmLoader(false);
    }
  };

  const openReorderModal = () => {
    setReorderType(activeType);
    if (activeType === "level") setReorderList([...badges]);
    else if (activeType === "achievement") setReorderList([...achievementBadges]);
    else setReorderList([...featureUnlocks]);
    setReorderModalOpen(true);
  };

  const dailyTasksByDate = useMemo(() => {
    const groups = {};
    dailyTasks.forEach((task) => {
      const d = task.show_date || "";
      if (!groups[d]) groups[d] = [];
      groups[d].push(task);
    });
    const dates = Object.keys(groups).sort((a, b) => {
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b);
    });
    return dates.map((date) => ({ date, tasks: groups[date] }));
  }, [dailyTasks]);

  // Daily task table: show only 10 date groups per page (client-side pagination)
  const dailyTasksDatesForPage = useMemo(() => {
    const dates = todayUserTasksData.dates || [];
    const start = (todayUserTasksPage - 1) * todayUserTasksPageSize;
    return dates.slice(start, start + todayUserTasksPageSize);
  }, [todayUserTasksData.dates, todayUserTasksPage, todayUserTasksPageSize]);

  const dailyTasksTotalPages = useMemo(() => {
    const total = (todayUserTasksData.dates || []).length;
    return Math.max(1, Math.ceil(total / todayUserTasksPageSize));
  }, [todayUserTasksData.dates, todayUserTasksPageSize]);

  const uploadDailyTaskFileCore = async (file) => {
    if (!file) return;
    setUploadDailyTaskError("");
    setUploadDailyTaskLoading(true);
    try {
      await GamificationService.uploadDailyTasks(file);
      await fetchDailyTasks();
      setTodayUserTasksPage(1);
      await fetchTodayUserTasks();
      if (dailyTaskFileInputRef.current) dailyTaskFileInputRef.current.value = "";
      setUploadDailyTaskModalOpen(false);
      Toast("success", "Daily tasks uploaded successfully");
    } catch (err) {
      console.error("Error uploading daily tasks CSV:", err);
      setUploadDailyTaskError(
        err?.response?.data?.message || "Upload failed. Please try again."
      );
      Toast("error", err?.response?.data?.message || "Upload failed. Please try again.");
    } finally {
      setUploadDailyTaskLoading(false);
    }
  };

  const handleUploadDailyTaskFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadDailyTaskFileCore(file);
  };

  const openScheduleModal = () => {
    const today = new Date().toISOString().slice(0, 10);
    setCurrentScheduleCalendarMonth(new Date());
    setScheduleForm({
      show_date: today,
      tasks: [
        {
          action: "",
          morning_push_title: "Morning Task",
          morning_push_header: "",
          afternoon_push_title: "Afternoon Task",
          afternoon_push_header: "",
          evening_push_title: "Evening Task",
          evening_push_header: "",
        },
      ],
    });
    setScheduleError("");
    setScheduleModalOpen(true);
  };

  const scheduleFormAddTask = () => {
    setScheduleForm((prev) => ({
      ...prev,
      tasks: [
        ...prev.tasks,
        {
          action: "",
          morning_push_title: "Morning Task",
          morning_push_header: "",
          afternoon_push_title: "Afternoon Task",
          afternoon_push_header: "",
          evening_push_title: "Evening Task",
          evening_push_header: "",
        },
      ],
    }));
  };

  const scheduleFormRemoveTask = (index) => {
    setScheduleForm((prev) => ({
      ...prev,
      tasks: prev.tasks.filter((_, i) => i !== index),
    }));
  };

  const scheduleFormUpdateTask = (taskIndex, field, value) => {
    setScheduleForm((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t, i) =>
        i === taskIndex ? { ...t, [field]: value } : t
      ),
    }));
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    setScheduleError("");
    if (!scheduleForm.show_date?.trim()) {
      setScheduleError("Show date is required.");
      return;
    }
    const validTasks = scheduleForm.tasks.filter((t) => t.action?.trim());
    if (validTasks.length < 3) {
      setScheduleError("Add at least 3 tasks with an action selected.");
      return;
    }
    setScheduleLoading(true);
    try {
      const payload = {
        show_date: scheduleForm.show_date.trim(),
        tasks: validTasks.map((t) => ({
          action: t.action.trim(),
          morning_push_header: t.morning_push_header?.trim() ?? "",
          afternoon_push_header: t.afternoon_push_header?.trim() ?? "",
          evening_push_header: t.evening_push_header?.trim() ?? "",
          morning_push_title: t.morning_push_title?.trim() ?? "Morning Task",
          afternoon_push_title: t.afternoon_push_title?.trim() ?? "Afternoon Task",
          evening_push_title: t.evening_push_title?.trim() ?? "Evening Task",
        })),
      };
      await GamificationService.scheduleDailyTasks(payload);
      await fetchDailyTasks();
      setTodayUserTasksPage(1);
      await fetchTodayUserTasks();
      setScheduleModalOpen(false);
    } catch (err) {
      console.error("Error scheduling daily tasks:", err);
      setScheduleError(
        err?.response?.data?.message || "Failed to schedule. Please try again."
      );
    } finally {
      setScheduleLoading(false);
    }
  };

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDndDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = reorderList.findIndex((item) => item._id === active.id);
    const newIndex = reorderList.findIndex((item) => item._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setReorderList(arrayMove(reorderList, oldIndex, newIndex));
  };

  const handleSaveReorder = async () => {
    setReorderLoading(true);
    try {
      const items = reorderList.map((item, order) => ({ _id: item._id, order }));
      if (reorderType === "level") {
        await GamificationService.reorderLevels(items);
        await fetchBadges();
      } else if (reorderType === "achievement") {
        await GamificationService.reorderAchievementBadges(items);
        await fetchAchievementBadges();
      } else {
        await GamificationService.reorderFeatureUnlocks(items);
        await fetchFeatureUnlocks();
      }
      setReorderModalOpen(false);
      setReorderList([]);
    } catch (err) {
      console.error("Error reordering:", err);
    } finally {
      setReorderLoading(false);
    }
  };

  useEffect(() => {
    fetchBadges();
    fetchAchievementBadges();
    fetchFeatureUnlocks();
    fetchDailyTasks();
    fetchTodayUserTasks();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (visitedGamificationTypesRef.current.has(activeType)) {
      return;
    }

    visitedGamificationTypesRef.current.add(activeType);

    if (activeType === "achievement") {
      fetchAchievementBadges(true);
    } else if (activeType === "feature") {
      fetchFeatureUnlocks(true);
    } else if (activeType === "how_to_earn_xp") {
      fetchDailyTasks(true);
    } else if (activeType === "daily_tasks") {
      fetchTodayUserTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType]);

  // Daily tasks list: we fetch all dates once and paginate client-side (10 per page)

  // Click outside handler for delete popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupOpenDelete && deleteRef.current && !deleteRef.current.contains(event.target)) {
        setPopupOpenDelete(false);
        setBadgeToDelete(null);
        document.body.style.overflow = "unset";
      }
    };

    if (popupOpenDelete) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popupOpenDelete]);

  // Click outside handler for schedule date picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showScheduleDatePicker && scheduleDatePickerRef.current && !scheduleDatePickerRef.current.contains(event.target)) {
        setShowScheduleDatePicker(false);
      }
    };
    if (showScheduleDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showScheduleDatePicker]);

  // When opening schedule date picker, show calendar month of selected date
  useEffect(() => {
    if (showScheduleDatePicker && scheduleForm.show_date) {
      const d = new Date(scheduleForm.show_date + "T12:00:00");
      if (!isNaN(d.getTime())) setCurrentScheduleCalendarMonth(d);
    }
  }, [showScheduleDatePicker]);

  const SortableReorderItem = ({ id, item, index }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 50 : "auto",
    };
    const imageUrl = item.badge_url || item.image_url;
    const label = item.title || item.name;
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="rounded-lg p-4 border border-white/10 bg-white/5 hover:border-white/20 cursor-grab active:cursor-grabbing flex items-center gap-4"
      >
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#369FFF]/20 flex items-center justify-center text-white font-bold text-sm">
          {index + 1}
        </div>
        <div className="flex-shrink-0">
          {imageUrl ? (
            <img src={imageUrl} alt={label} className="h-12 w-12 rounded-lg object-cover border border-white/10" />
          ) : (
            <div className="h-12 w-12 rounded-lg bg-white/10 flex items-center justify-center text-gray-500 text-xs">—</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate">{label || "—"}</p>
        </div>
        <div className="flex-shrink-0 text-gray-400" {...attributes} {...listeners}>
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
          </svg>
        </div>
      </div>
    );
  };

  return (
    <div className="-mt-2 grid h-full grid-cols-1 gap-5">
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        <Card extra={"w-full h-full px-4 sm:px-6 pb-6 overflow-hidden"}>
          <div className="mt-4 mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">
                {activeType === "level"
                  ? "Level Badges"
                  : activeType === "feature"
                    ? "Feature Unlocks"
                    : activeType === "how_to_earn_xp"
                      ? "How to earn XP"
                      : activeType === "daily_tasks"
                        ? "Daily Task"
                        : "Achievement Badges"}
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                {activeType === "level"
                  ? "View all levels and XP requirements for user badges."
                  : activeType === "feature"
                    ? "View feature unlocks and their badge settings."
                    : activeType === "how_to_earn_xp"
                      ? "Ways users can earn XP and rewards."
                      : activeType === "daily_tasks"
                        ? "Daily tasks by date with CSV upload."
                        : "View all achievement badges based on user actions and milestones."}
              </p>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto sm:justify-end flex-wrap">
              <div className="w-full sm:w-[180px]">
                <Select
                  className="!text-white"
                  classNamePrefix="select"
                  options={BADGE_TYPE_OPTIONS}
                  value={BADGE_TYPE_OPTIONS.find((o) => o.value === activeType)}
                  onChange={(option) => {
                    if (!option || option.value === activeType) return;
                    setActiveType(option.value);
                  }}
                  styles={colourStyles}
                  isSearchable={false}
                />
              </div>
              <input
                type="file"
                ref={dailyTaskFileInputRef}
                accept=".csv"
                className="hidden"
                onChange={handleUploadDailyTaskFile}
              />
              {activeType === "daily_tasks" && (
                <>
                  <button
                    type="button"
                    onClick={openScheduleModal}
                    className="rounded-full bg-orangePrimary px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                  >
                    Create daily task
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploadDailyTaskModalOpen(true)}
                    disabled={uploadDailyTaskLoading}
                    className="rounded-full bg-orangePrimary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Upload Daily Task
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={openReorderModal}
                disabled={
                  activeType === "daily_tasks" ||
                  activeType === "how_to_earn_xp" ||
                  (activeType === "level" && badges.length === 0) ||
                  (activeType === "achievement" && achievementBadges.length === 0) ||
                  (activeType === "feature" && featureUnlocks.length === 0)
                }
                className="rounded-full bg-orangePrimary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Edit Order
              </button>
            </div>
          </div>

          {uploadDailyTaskError && (activeType === "daily_tasks" || activeType === "how_to_earn_xp") && (
            <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300">
              {uploadDailyTaskError}
            </div>
          )}

          {deleteError && (
            <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300">
              {deleteError}
            </div>
          )}

          <div
            className={`table-scroll mt-4 relative ${
              (activeType === "level"
                ? isLoading
                : activeType === "achievement"
                  ? achievementLoading
                  : activeType === "feature"
                    ? featureLoading
                    : activeType === "how_to_earn_xp"
                      ? dailyTasksLoading
                      : todayUserTasksLoading)
                ? "h-[75vh] overflow-hidden"
                : "overflow-x-auto overflow-y-hidden"
            }`}
          >
            {activeType === "level" && isLoading && (
              <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                <img src={Loader} alt="Loading..." className="h-16 w-16" />
              </div>
            )}
            {activeType === "achievement" && achievementLoading && (
              <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                <img src={Loader} alt="Loading..." className="h-16 w-16" />
              </div>
            )}
            {activeType === "feature" && featureLoading && (
              <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                <img src={Loader} alt="Loading..." className="h-16 w-16" />
              </div>
            )}
            {activeType === "how_to_earn_xp" && dailyTasksLoading && (
              <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                <img src={Loader} alt="Loading..." className="h-16 w-16" />
              </div>
            )}
            {activeType === "daily_tasks" && todayUserTasksLoading && (
              <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                <img src={Loader} alt="Loading..." className="h-16 w-16" />
              </div>
            )}
            <table
              className={`w-full ${
                (activeType === "level"
                  ? isLoading
                  : activeType === "achievement"
                    ? achievementLoading
                    : activeType === "feature"
                      ? featureLoading
                      : activeType === "how_to_earn_xp"
                        ? dailyTasksLoading
                        : todayUserTasksLoading)
                  ? "table-fixed"
                  : ""
              }`}
              style={
                (activeType === "level"
                  ? isLoading
                  : activeType === "achievement"
                    ? achievementLoading
                    : activeType === "feature"
                      ? featureLoading
                      : activeType === "how_to_earn_xp"
                        ? dailyTasksLoading
                        : todayUserTasksLoading)
                  ? { tableLayout: "fixed", width: "100%" }
                  : {
                      tableLayout: "auto",
                      minWidth: "max-content",
                      width: "100%",
                    }
              }
            >
              <thead
                className={
                  (activeType === "level"
                    ? isLoading
                    : activeType === "achievement"
                      ? achievementLoading
                      : activeType === "feature"
                        ? featureLoading
                        : activeType === "how_to_earn_xp"
                          ? dailyTasksLoading
                          : todayUserTasksLoading)
                    ? "hidden"
                    : ""
                }
              >
                {activeType === "level" ? (
                  <tr className="!border-px !border-gray-400">
                    <th className="w-[70px] min-w-[60px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Level
                    </th>
                    <th className="w-[100px] min-w-[80px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Badge
                    </th>
                    <th className="w-[200px] min-w-[150px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Title
                    </th>
                    <th className="w-[150px] min-w-[120px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      XP Required
                    </th>
                    <th className="w-[260px] min-w-[200px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Description
                    </th>
                    <th className="w-[140px] min-w-[120px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Actions
                    </th>
                  </tr>
                ) : activeType === "feature" ? (
                  <tr className="!border-px !border-gray-400">
                    <th className="w-[70px] min-w-[60px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Level
                    </th>
                    <th className="w-[100px] min-w-[80px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Badge
                    </th>
                    <th className="w-[120px] min-w-[90px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Unlock Type
                    </th>
                    <th className="w-[100px] min-w-[80px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Duration
                    </th>
                    <th className="w-[180px] min-w-[140px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Title
                    </th>
                    <th className="w-[220px] min-w-[180px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Description
                    </th>
                    <th className="w-[140px] min-w-[120px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Actions
                    </th>
                  </tr>
                ) : activeType === "how_to_earn_xp" ? (
                  <tr className="!border-px !border-gray-400">
                    <th className="w-[90px] min-w-[70px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Icon
                    </th>
                    <th className="w-[180px] min-w-[140px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Title
                    </th>
                    <th className="w-[220px] min-w-[180px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Description
                    </th>
                    <th className="w-[90px] min-w-[70px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      XP Reward
                    </th>
                    <th className="w-[120px] min-w-[100px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Actions
                    </th>
                  </tr>
                ) : activeType === "daily_tasks" ? (
                  <tr className="!border-px !border-gray-400">
                    <th className="w-[140px] min-w-[100px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Title
                    </th>
                    <th className="w-[180px] min-w-[140px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Morning notification
                    </th>
                    <th className="w-[180px] min-w-[140px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Afternoon notification
                    </th>
                    <th className="w-[180px] min-w-[140px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Evening notification
                    </th>
                    <th className="w-[90px] min-w-[70px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      XP Reward
                    </th>
                    <th className="w-[120px] min-w-[100px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Actions
                    </th>
                  </tr>
                ) : (
                  <tr className="!border-px !border-gray-400">
                    <th className="w-[100px] min-w-[80px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Badge
                    </th>
                    <th className="w-[200px] min-w-[150px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Name
                    </th>
                    <th className="w-[150px] min-w-[120px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Threshold
                    </th>
                    <th className="w-[260px] min-w-[200px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Description
                    </th>
                    <th className="w-[140px] min-w-[120px] text-sm font-bold uppercase text-white text-left align-middle px-2 sm:px-3">
                      Actions
                    </th>
                  </tr>
                )}
              </thead>
              <tbody>
                {                  (activeType === "level"
                    ? isLoading
                    : activeType === "achievement"
                      ? achievementLoading
                      : activeType === "feature"
                        ? featureLoading
                        : activeType === "how_to_earn_xp"
                          ? dailyTasksLoading
                          : todayUserTasksLoading) ? (
                  <tr className="md:hidden">
                    <td colSpan={activeType === "feature" ? 7 : activeType === "achievement" ? 5 : activeType === "daily_tasks" ? 6 : activeType === "how_to_earn_xp" ? 5 : 6} className="py-6">
                      <div className="flex justify-center">
                        <img src={Loader} alt="Loading..." className="h-16 w-16" />
                      </div>
                    </td>
                  </tr>
                ) : activeType === "level" ? (
                  badges.length === 0 ? (
                    <tr>
                    <td colSpan={6} className="py-4 text-center">
                        <p className="my-3">No badges found</p>
                      </td>
                    </tr>
                  ) : (
                    badges.map((badge) => (
                      <tr
                        key={badge._id}
                        className="border-b border-[#fbfbfb1a] last:border-none"
                      >
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <span className="text-white text-sm">{badge.level}</span>
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          {badge.badge_url ? (
                            <img
                              src={badge.badge_url}
                              alt={badge.title}
                              className="h-12 w-12 rounded-lg object-cover"
                            />
                          ) : (
                            <span className="text-gray-500 text-sm">--</span>
                          )}
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <span className="text-white text-sm font-semibold">
                            {badge.title}
                          </span>
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <span className="text-white text-sm">
                            {badge.xp_required}
                          </span>
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <div
                            className="max-w-[260px] sm:max-w-[320px] truncate"
                            title={badge.description || ""}
                          >
                            <span className="text-gray-white text-sm">
                              {badge.description || "--"}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenView(badge, "level")}
                              className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] duration-150 hover:bg-white/20"
                              title="View details"
                            >
                              <IoEye />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(badge, "level")}
                              className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FF80001A]/10 text-[#FF8000] duration-150 hover:bg-[#FF80001A]/20"
                              title="Edit Badge"
                            >
                              <AiFillEdit />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )
                ) : activeType === "feature" ? (
                  featureUnlocks.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-4 text-center">
                        <p className="my-3">No feature unlocks found</p>
                      </td>
                    </tr>
                  ) : (
                    featureUnlocks.map((item) => (
                      <tr
                        key={item._id}
                        className="border-b border-[#fbfbfb1a] last:border-none"
                      >
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <span className="text-white text-sm">{item.level}</span>
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          {item.badge_url ? (
                            <img
                              src={item.badge_url}
                              alt={item.title}
                              className="h-12 w-12 rounded-lg object-cover"
                            />
                          ) : (
                            <span className="text-gray-500 text-sm">--</span>
                          )}
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <span className="text-white text-sm capitalize">
                            {item.unlock_type || "--"}
                          </span>
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <span className="text-white text-sm">
                            {item.duration_days != null ? `${item.duration_days}d` : "--"}
                          </span>
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <span className="text-white text-sm font-semibold">
                            {item.title}
                          </span>
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <div
                            className="max-w-[220px] sm:max-w-[280px] truncate"
                            title={item.description || ""}
                          >
                            <span className="text-gray-400 text-sm">
                              {item.description || "--"}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenView(item, "feature")}
                              className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] duration-150 "
                              title="View details"
                            >
                              <IoEye />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(item, "feature")}
                              className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FF80001A]/10 text-[#FF8000] duration-150 "
                              title="Edit Feature Unlock"
                            >
                              <AiFillEdit />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )
                ) : activeType === "how_to_earn_xp" ? (
                  dailyTasks.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-4 text-center">
                        <p className="my-3">No how to earn XP items found</p>
                      </td>
                    </tr>
                  ) : (
                    dailyTasks.map((task) => (
                      <tr
                        key={task._id}
                        className="border-b border-[#fbfbfb1a] last:border-none"
                      >
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          {task.icon && (task.icon.startsWith("http") || task.icon.startsWith("data:")) ? (
                            <img src={task.icon} alt="" className="h-10 w-10 rounded-lg object-cover border border-white/10" />
                          ) : (
                            <span className="text-white text-sm">{task.icon || "--"}</span>
                          )}
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <span className="text-white text-sm font-semibold">{task.title}</span>
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <div className="max-w-[220px] sm:max-w-[280px] truncate" title={task.description || ""}>
                            <span className="text-gray-400 text-sm">{task.description || "--"}</span>
                          </div>
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <span className="text-white text-sm">{task.xp_reward ?? "--"}</span>
                        </td>
                        <td className="py-3 px-2 sm:px-3 text-left align-middle">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenView(task, "how_to_earn_xp")}
                              className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] duration-150 "
                              title="View details"
                            >
                              <IoEye />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(task, "how_to_earn_xp")}
                              className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FF80001A]/10 text-[#FF8000] duration-150 hover:bg-[#FF80001A]/20"
                              title="Edit"
                            >
                              <AiFillEdit />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )
                ) : activeType === "daily_tasks" ? (
                  todayUserTasksData.dates.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-4 text-center">
                        <p className="my-3">No daily tasks found</p>
                      </td>
                    </tr>
                  ) : (
                    dailyTasksDatesForPage.map(({ date, tasks }) => (
                      <React.Fragment key={date || "no-date"}>
                        <tr className="bg-white/5">
                          <td colSpan={6} className="py-2.5 px-3 text-sm font-semibold text-white border-b border-white/10">
                            {date ? new Date(date + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric" }) : "No date"}
                          </td>
                        </tr>
                        {tasks.map((entry) => {
                          const task = entry.task || entry;
                          return (
                            <tr
                              key={task._id}
                              className="border-b border-[#fbfbfb1a] last:border-none"
                            >
                              <td className="py-3 px-2 sm:px-3 text-left align-middle">
                                <span className="text-white text-sm font-semibold">{task.title}</span>
                              </td>
                              <td className="py-3 px-2 sm:px-3 text-left align-middle">
                                <div className="max-w-[180px] truncate" title={[entry.morning_push_title, entry.morning_push_header].filter(Boolean).join(": ")}>
                                  <span className="text-white text-xs font-semibold block">{entry.morning_push_title || "Morning"}</span>
                                  <span className="text-gray-400 text-sm">{entry.morning_push_header || "--"}</span>
                                </div>
                              </td>
                              <td className="py-3 px-2 sm:px-3 text-left align-middle">
                                <div className="max-w-[180px] truncate" title={[entry.afternoon_push_title, entry.afternoon_push_header].filter(Boolean).join(": ")}>
                                  <span className="text-white text-xs font-semibold block">{entry.afternoon_push_title || "Afternoon"}</span>
                                  <span className="text-gray-400 text-sm">{entry.afternoon_push_header || "--"}</span>
                                </div>
                              </td>
                              <td className="py-3 px-2 sm:px-3 text-left align-middle">
                                <div className="max-w-[180px] truncate" title={[entry.evening_push_title, entry.evening_push_header].filter(Boolean).join(": ")}>
                                  <span className="text-white text-xs font-semibold block">{entry.evening_push_title || "Evening"}</span>
                                  <span className="text-gray-400 text-sm">{entry.evening_push_header || "--"}</span>
                                </div>
                              </td>
                              <td className="py-3 px-2 sm:px-3 text-left align-middle">
                                <span className="text-white text-sm">{task.xp_reward ?? "--"}</span>
                              </td>
                              <td className="py-3 px-2 sm:px-3 text-left align-middle">
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleOpenView(entry, "daily_tasks")}
                                    className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] duration-150 "
                                    title="View details"
                                  >
                                    <IoEye />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleOpenEdit(entry, "daily_tasks", date)}
                                    className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FF80001A]/10 text-[#FF8000] duration-150 "
                                    title="Edit Daily Task"
                                  >
                                    <AiFillEdit />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))
                  )
                ) : achievementBadges.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-4 text-center">
                      <p className="my-3">No achievement badges found</p>
                    </td>
                  </tr>
                ) : (
                  achievementBadges.map((badge) => (
                    <tr
                      key={badge._id}
                      className="border-b border-[#fbfbfb1a] last:border-none"
                    >
                      <td className="py-3 px-2 sm:px-3 text-left align-middle">
                        {badge.image_url ? (
                          <img
                            src={badge.image_url}
                            alt={badge.name}
                            className="h-12 w-12 rounded-lg object-cover"
                          />
                        ) : (
                          <span className="text-gray-500 text-sm">--</span>
                        )}
                      </td>
                      <td className="py-3 px-2 sm:px-3 text-left align-middle">
                        <span className="text-white text-sm font-semibold">
                          {badge.name}
                        </span>
                      </td>
                      <td className="py-3 px-2 sm:px-3 text-left align-middle">
                        <span className="text-white text-sm">
                          {badge.threshold ?? "--"}
                        </span>
                      </td>
                      <td className="py-3 px-2 sm:px-3 text-left align-middle">
                        <div
                          className="max-w-[260px] sm:max-w-[320px] truncate"
                          title={badge.description || ""}
                        >
                          <span className="text-gray-400 text-sm">
                            {badge.description || "--"}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-2 sm:px-3 text-left align-middle">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleOpenView(badge, "achievement")}
                            className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] duration-150 "
                            title="View details"
                          >
                            <IoEye />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(badge, "achievement")}
                            className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FF80001A]/10 text-[#FF8000] duration-150 "
                            title="Edit Achievement Badge"
                          >
                            <AiFillEdit />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {activeType === "daily_tasks" && !todayUserTasksLoading && todayUserTasksData.dates.length > 0 && (
              <div className="border-t border-white/10 px-3 py-3 sm:px-4 flex justify-end">
                <Pagination
                  page={todayUserTasksPage}
                  totalPages={dailyTasksTotalPages}
                  handlePageChange={(newPage) => {
                    setTodayUserTasksPage(newPage);
                  }}
                />
              </div>
            )}
          </div>
        </Card>
      </div>

      {reorderModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div
            className="mx-3 flex h-[80vh] w-full max-w-[600px] flex-col rounded-xl bg-navy-800 shadow-lg border border-white/10"
            ref={reorderModalRef}
          >
            <div className="flex justify-center border-b border-white/10 py-4 px-6 relative">
              <p className="text-xl font-semibold text-white">
                {reorderType === "level"
                  ? "Reorder Level Badges"
                  : reorderType === "achievement"
                    ? "Reorder Achievement Badges"
                    : "Reorder Feature Unlocks"}
              </p>
              <IoMdClose
                className="absolute right-6 cursor-pointer text-2xl text-white hover:text-gray-300"
                onClick={() => {
                  setReorderModalOpen(false);
                  setReorderList([]);
                }}
              />
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <p className="text-sm text-gray-300 mb-4">
                Drag and drop to reorder. Click Save Order to apply.
              </p>
              <DndContext
                sensors={dndSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDndDragEnd}
              >
                <SortableContext
                  items={reorderList.map((i) => i._id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {reorderList.map((item, index) => (
                      <SortableReorderItem key={item._id} id={item._id} item={item} index={index} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
            <div className="border-t border-white/10 px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setReorderModalOpen(false);
                  setReorderList([]);
                }}
                className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 font-semibold text-sm border border-white/20"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveReorder}
                disabled={reorderLoading}
                className="px-6 py-2.5 rounded-lg bg-orangePrimary text-white hover:opacity-90 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reorderLoading ? (
                  <img src={Loader} alt="Loading..." className="h-5 w-5 inline-block" />
                ) : (
                  "Save Order"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {uploadDailyTaskModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3">
          <div className="w-full max-w-[480px] rounded-2xl bg-navy-800 border border-white/10 p-5 sm:p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <p className="text-lg font-bold text-white">Upload daily tasks CSV</p>
              <button
                type="button"
                className="cursor-pointer text-2xl text-gray-400 hover:text-white disabled:opacity-40"
                disabled={uploadDailyTaskLoading}
                onClick={() => {
                  if (uploadDailyTaskLoading) return;
                  setUploadDailyTaskModalOpen(false);
                  setUploadDailyTaskError("");
                }}
              >
                ×
              </button>
            </div>
            {uploadDailyTaskError && (
              <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                {uploadDailyTaskError}
              </div>
            )}
            <div
              className={`flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-4 py-10 sm:px-6 sm:py-12 transition-colors ${
                uploadDailyTaskLoading ? "border-orangePrimary/60 bg-orangePrimary/5" : "border-white/20 bg-black/20 hover:border-white/40"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (uploadDailyTaskLoading) return;
                const file = e.dataTransfer.files?.[0];
                if (file) {
                  await uploadDailyTaskFileCore(file);
                }
              }}
            >
              {uploadDailyTaskLoading ? (
                <div className="flex flex-col items-center gap-3">
                  <img src={Loader} alt="Uploading..." className="h-10 w-10" />
                  <p className="text-sm text-white font-medium">Uploading daily tasks...</p>
                  <p className="text-xs text-gray-400">Please wait, this may take a few seconds.</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-200 font-medium mb-1">Drag &amp; drop CSV file here</p>
                  <p className="text-xs text-gray-400 mb-4">or</p>
                  <button
                    type="button"
                    onClick={() => dailyTaskFileInputRef.current?.click()}
                    className="rounded-full bg-orangePrimary px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                  >
                    Browse file
                  </button>
                  <p className="mt-3 text-[11px] text-gray-400">Only .csv files are supported.</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {scheduleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3">
          <div className="flex max-h-[80vh] w-full max-w-[640px] flex-col rounded-2xl bg-navy-800 shadow-2xl border border-white/10 min-h-0">
            <div className="flex shrink-0 items-center justify-between border-b border-white/10 py-4 px-6">
              <p className="text-xl font-bold text-white">Create daily task</p>
              <button
                type="button"
                className="cursor-pointer text-2xl text-gray-400 hover:text-white"
                onClick={() => { setScheduleModalOpen(false); setScheduleError(""); }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleScheduleSubmit} className="flex flex-1 min-h-0 flex-col overflow-hidden">
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
                <div className="rounded-xl border border-white/10 bg-white/5 p-5 sm:p-6 space-y-5">
                  {scheduleError && (
                    <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                      {scheduleError}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Show date *</label>
                    <div className="relative w-full" ref={scheduleDatePickerRef}>
                      <input
                        type="text"
                        placeholder="YYYY-MM-DD"
                        value={scheduleForm.show_date}
                        readOnly
                        onClick={() => setShowScheduleDatePicker((v) => !v)}
                        className="w-full rounded-xl border border-white/10 bg-navy-800 px-3 py-2.5 pr-10 text-sm text-white placeholder-[#8E8E8E] outline-none focus:border-white/20 cursor-pointer transition-colors"
                      />
                      {scheduleForm.show_date ? (
                        <MdClose
                          className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setScheduleForm((prev) => ({ ...prev, show_date: "" }));
                          }}
                        />
                      ) : (
                        <MdCalendarToday
                          className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white cursor-pointer"
                          onClick={() => setShowScheduleDatePicker((v) => !v)}
                        />
                      )}
                      {showScheduleDatePicker && (
                        <div className="absolute top-full left-0 mt-2 w-full sm:w-96 max-w-[calc(100vw-3rem)] bg-navy-800 border border-white/10 rounded-xl shadow-lg z-[9999] datepicker-popup">
                          <div className="p-4">
                              <div className="flex justify-between items-center mb-4">
                                <button
                                  type="button"
                                  onClick={() => setCurrentScheduleCalendarMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1))}
                                  className="text-white hover:text-[#8E8E8E]"
                                >
                                  <MdChevronLeft className="h-5 w-5" />
                                </button>
                                <h3 className="text-white font-semibold">
                                  {SCHEDULE_DATE_MONTHS[currentScheduleCalendarMonth.getMonth()]} {currentScheduleCalendarMonth.getFullYear()}
                                </h3>
                                <button
                                  type="button"
                                  onClick={() => setCurrentScheduleCalendarMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1))}
                                  className="text-white hover:text-[#8E8E8E]"
                                >
                                  <MdChevronRight className="h-5 w-5" />
                                </button>
                              </div>
                              <div className="grid grid-cols-7 gap-1 mb-2">
                                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                                  <div key={day} className="text-center text-white text-sm font-medium py-2">
                                    {day}
                                  </div>
                                ))}
                              </div>
                              <div className="grid grid-cols-7 gap-1">
                                {getDaysInMonthForSchedule(currentScheduleCalendarMonth).map((dayObj, index) => {
                                  const dateStr = dayObj.date.toISOString().slice(0, 10);
                                  const isSelected = scheduleForm.show_date === dateStr;
                                  return (
                                    <button
                                      key={index}
                                      type="button"
                                      onClick={() => {
                                        setScheduleForm((prev) => ({ ...prev, show_date: dateStr }));
                                        setShowScheduleDatePicker(false);
                                      }}
                                      className={`h-7 w-7 text-sm rounded ${dayObj.isCurrentMonth ? "text-white" : "text-gray-500"} ${
                                        isSelected ? "bg-orangePrimary/70 text-white" : "hover:bg-white/10"
                                      }`}
                                    >
                                      {dayObj.day}
                                    </button>
                                  );
                                })}
                              </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-sm font-medium text-gray-300">Tasks</span>
                    <button
                      type="button"
                      onClick={scheduleFormAddTask}
                      className="rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20"
                    >
                      + Add task
                    </button>
                  </div>
                  {scheduleForm.tasks.map((task, idx) => (
                    <div key={idx} className="rounded-xl border border-white/10 bg-black/20 p-4 sm:p-5 space-y-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <label className="block text-sm font-medium text-gray-300 mb-1">Action</label>
                          <Select
                            className="!text-white"
                            classNamePrefix="select"
                            options={DAILY_TASK_ACTIONS}
                            value={DAILY_TASK_ACTIONS.find((o) => o.value === task.action) || null}
                            onChange={(opt) => scheduleFormUpdateTask(idx, "action", opt?.value ?? "")}
                            styles={colourStyles}
                            isSearchable
                            placeholder="Select action"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => scheduleFormRemoveTask(idx)}
                          disabled={scheduleForm.tasks.length <= 3}
                          className="shrink-0 mt-6 rounded-lg bg-red-500/20 px-3 py-2 text-sm text-red-300 hover:bg-red-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Morning title</label>
                          <input
                            type="text"
                            value={task.morning_push_title}
                            onChange={(e) => scheduleFormUpdateTask(idx, "morning_push_title", e.target.value)}
                            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                            placeholder="Morning Task"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Afternoon title</label>
                          <input
                            type="text"
                            value={task.afternoon_push_title}
                            onChange={(e) => scheduleFormUpdateTask(idx, "afternoon_push_title", e.target.value)}
                            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                            placeholder="Afternoon Task"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Evening title</label>
                          <input
                            type="text"
                            value={task.evening_push_title}
                            onChange={(e) => scheduleFormUpdateTask(idx, "evening_push_title", e.target.value)}
                            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                            placeholder="Evening Task"
                          />
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Morning description</label>
                          <input
                            type="text"
                            value={task.morning_push_header}
                            onChange={(e) => scheduleFormUpdateTask(idx, "morning_push_header", e.target.value)}
                            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                            placeholder="Morning push text"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Afternoon description</label>
                          <input
                            type="text"
                            value={task.afternoon_push_header}
                            onChange={(e) => scheduleFormUpdateTask(idx, "afternoon_push_header", e.target.value)}
                            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                            placeholder="Afternoon push text"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Evening description</label>
                          <input
                            type="text"
                            value={task.evening_push_header}
                            onChange={(e) => scheduleFormUpdateTask(idx, "evening_push_header", e.target.value)}
                            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                            placeholder="Evening push text"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="shrink-0 border-t border-white/10 px-6 py-4 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setScheduleModalOpen(false); setScheduleError(""); }}
                  className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 font-semibold text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={scheduleLoading || scheduleForm.tasks.length < 3}
                  className="px-6 py-2.5 rounded-lg bg-orangePrimary text-white hover:opacity-90 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {scheduleLoading ? (
                    <img src={Loader} alt="Loading..." className="h-5 w-5 inline-block" />
                  ) : (
                    "Create schedule"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewModalOpen && viewItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="mx-3 flex max-h-[90vh] w-full max-w-[560px] flex-col rounded-2xl bg-navy-800 shadow-2xl border border-white/10">
            <div className="relative flex items-center justify-between border-b border-white/10 py-5 px-6">
              <div className="flex items-center gap-3">
                <div className="h-1 w-8 rounded-full bg-gradient-to-r from-blue-400 to-blue-600"></div>
                <p className="text-xl font-bold text-white">View details</p>
              </div>
              <button
                type="button"
                className="cursor-pointer text-2xl text-gray-400 hover:text-white transition-colors duration-200"
                onClick={() => { setViewModalOpen(false); setViewItem(null); }}
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="space-y-4">
                {viewType === "level" && (
                  <>
                    <DetailRow label="Level" value={viewItem.level} />
                    <DetailRow label="Title" value={viewItem.title} />
                    <DetailRow label="XP Required" value={viewItem.xp_required} />
                    <DetailRow label="Description" value={viewItem.description} isLong />
                    {viewItem.badge_url && (
                      <div>
                        <span className="block text-sm font-medium text-gray-400 mb-1">Badge</span>
                        <img src={viewItem.badge_url} alt={viewItem.title} className="h-16 w-16 rounded-lg object-cover" />
                      </div>
                    )}
                  </>
                )}
                {viewType === "achievement" && (
                  <>
                    <DetailRow label="Name" value={viewItem.name} />
                    <DetailRow label="Threshold" value={viewItem.threshold} />
                    <DetailRow label="Description" value={viewItem.description} isLong />
                    {viewItem.image_url && (
                      <div>
                        <span className="block text-sm font-medium text-gray-400 mb-1">Badge</span>
                        <img src={viewItem.image_url} alt={viewItem.name} className="h-16 w-16 rounded-lg object-cover" />
                      </div>
                    )}
                  </>
                )}
                {viewType === "feature" && (
                  <>
                    <DetailRow label="Level" value={viewItem.level} />
                    <DetailRow label="Unlock type" value={viewItem.unlock_type} />
                    <DetailRow label="Duration" value={viewItem.duration_days != null ? `${viewItem.duration_days} days` : "--"} />
                    <DetailRow label="Title" value={viewItem.title} />
                    <DetailRow label="Description" value={viewItem.description} isLong />
                    {viewItem.badge_url && (
                      <div>
                        <span className="block text-sm font-medium text-gray-400 mb-1">Badge</span>
                        <img src={viewItem.badge_url} alt={viewItem.title} className="h-16 w-16 rounded-lg object-cover" />
                      </div>
                    )}
                  </>
                )}
                {viewType === "how_to_earn_xp" && (
                  <>
                    <div>
                      <span className="block text-xs font-medium text-gray-400 mb-0.5">Icon</span>
                      {viewItem.icon && (viewItem.icon.startsWith("http") || viewItem.icon.startsWith("data:")) ? (
                        <img src={viewItem.icon} alt="" className="h-14 w-14 rounded-lg object-cover border border-white/10 mt-1" />
                      ) : (
                        <span className="text-white text-sm">{viewItem.icon ?? "--"}</span>
                      )}
                    </div>
                    <DetailRow label="Title" value={viewItem.title} />
                    <DetailRow label="Description" value={viewItem.description} isLong />
                    <DetailRow label="XP Reward" value={viewItem.xp_reward} />
                  </>
                )}
                {viewType === "daily_tasks" && (() => {
                  const task = viewItem.task || viewItem;
                  const e = viewItem.task ? viewItem : null;
                  return (
                    <>
                      <DetailRow label="Action" value={task.action} />
                      <DetailRow label="Title" value={task.title} />
                      <DetailRow label="Prompt to achieve" value={task.prompt_to_achieve} />
                      <DetailRow label="XP Reward" value={task.xp_reward} />
                      <div className="border-t border-white/10 pt-4 mt-4">
                        <p className="text-sm font-semibold text-white mb-2">Morning notification</p>
                        <DetailRow label="Title" value={e?.morning_push_title} />
                        <DetailRow label="Header" value={e?.morning_push_header} isLong />
                      </div>
                      <div className="border-t border-white/10 pt-4">
                        <p className="text-sm font-semibold text-white mb-2">Afternoon notification</p>
                        <DetailRow label="Title" value={e?.afternoon_push_title} />
                        <DetailRow label="Header" value={e?.afternoon_push_header} isLong />
                      </div>
                      <div className="border-t border-white/10 pt-4">
                        <p className="text-sm font-semibold text-white mb-2">Evening notification</p>
                        <DetailRow label="Title" value={e?.evening_push_title} />
                        <DetailRow label="Header" value={e?.evening_push_header} isLong />
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="mx-3 flex max-h-[90vh] w-full max-w-[500px] flex-col rounded-2xl bg-navy-800 shadow-2xl border border-white/10">
            {/* Header */}
            <div className="relative flex items-center justify-between border-b border-white/10 py-5 px-6">
              <div className="flex items-center gap-3">
                <div className="h-1 w-8 rounded-full bg-gradient-to-r from-blue-400 to-blue-600"></div>
                <p className="text-xl font-bold text-white">
                  {editType === "daily_tasks" ? "Edit Daily Task" : editType === "how_to_earn_xp" ? "Edit How to earn XP" : "Edit Badge"}
                </p>
              </div>
              <button
                type="button"
                className="cursor-pointer text-2xl text-gray-400 hover:text-white transition-colors duration-200"
                onClick={() => {
                  setEditModalOpen(false);
                  setSelectedBadge(null);
                  setEditingScheduleDate(null);
                  setEditingOriginalAction(null);
                  setEditForm({ title: "", description: "", xp_required: "", badge_url: "", is_active: true, threshold: "", prompt_to_achieve: "", xp_reward: "", icon: "", action: "", morning_push_title: "", morning_push_header: "", afternoon_push_title: "", afternoon_push_header: "", evening_push_title: "", evening_push_header: "" });
                  setEditError("");
                }}
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <form onSubmit={handleUpdateBadge} autoComplete="off">
                <div className="space-y-5">
                  {editType !== "daily_tasks" && editType !== "how_to_earn_xp" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Title*
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={editForm.title}
                        onChange={handleEditInputChange}
                        className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                        placeholder="Enter badge title"
                      />
                    </div>
                  )}

                  {editType === "how_to_earn_xp" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Icon</label>
                        <input
                          type="file"
                          accept="image/*"
                          id="how_to_earn_xp_icon_upload"
                          style={{ display: "none" }}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setImageUploading(true);
                            setEditError("");
                            try {
                              const formData = new FormData();
                              formData.append("files", file);
                              const response = await EventService.fileUpload(formData);
                              // Handle different possible response structures
                              const url = response?.data?.files?.[0] || response?.data?.data?.files?.[0] || response?.data?.url || response?.data;
                              if (url && typeof url === 'string') {
                                setEditForm((prev) => ({ ...prev, icon: url }));
                              } else {
                                console.error("Unexpected upload response structure:", response);
                                setEditError("Icon image upload failed: Invalid response format.");
                              }
                            } catch (err) {
                              console.error("Upload error:", err);
                              setEditError("Icon image upload failed. Please try again.");
                            } finally {
                              setImageUploading(false);
                            }
                            e.target.value = "";
                          }}
                        />
                        <div className="flex items-center gap-3">
                          {editForm.icon ? (
                            typeof editForm.icon === "string" && (editForm.icon.startsWith("http") || editForm.icon.startsWith("data:")) ? (
                              <img src={editForm.icon} alt="Icon" className="h-14 w-14 rounded-lg object-cover border border-white/10" />
                            ) : (
                              <span className="text-white text-sm">{editForm.icon}</span>
                            )
                          ) : (
                            <div className="h-14 w-14 rounded-lg border border-white/10 bg-black/40 flex items-center justify-center text-gray-500 text-xs">No icon</div>
                          )}
                          <button
                            type="button"
                            onClick={() => document.getElementById("how_to_earn_xp_icon_upload")?.click()}
                            disabled={imageUploading}
                            className="rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white hover:bg-white/20 disabled:opacity-50"
                          >
                            {imageUploading ? "Uploading..." : "Upload image"}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Title *</label>
                        <input
                          type="text"
                          name="title"
                          value={editForm.title}
                          onChange={handleEditInputChange}
                          className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                          placeholder="Enter title"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                        <textarea
                          name="description"
                          value={editForm.description}
                          onChange={handleEditInputChange}
                          className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary min-h-[80px] resize-y"
                          placeholder="Enter description"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">XP Reward</label>
                        <input
                          type="number"
                          name="xp_reward"
                          value={editForm.xp_reward}
                          onChange={handleEditInputChange}
                          min="0"
                          className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                          placeholder="e.g. 20"
                        />
                      </div>
                    </>
                  )}

                  {editType === "daily_tasks" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Action</label>
                        <Select
                          className="!text-white"
                          classNamePrefix="select"
                          options={DAILY_TASK_ACTIONS}
                          value={DAILY_TASK_ACTIONS.find((o) => o.value === editForm.action) || null}
                          onChange={(opt) => setEditForm((prev) => ({ ...prev, action: opt?.value ?? "" }))}
                          styles={colourStyles}
                          isSearchable
                          placeholder="Select action"
                        />
                      </div>
                      <div className="border-b border-white/10 pb-3">
                        <p className="text-sm font-semibold text-white mb-2">Morning notification</p>
                        <div className="space-y-2">
                          <input
                            type="text"
                            name="morning_push_title"
                            value={editForm.morning_push_title}
                            onChange={handleEditInputChange}
                            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                            placeholder="Title (e.g. Morning Task)"
                          />
                          <input
                            type="text"
                            name="morning_push_header"
                            value={editForm.morning_push_header}
                            onChange={handleEditInputChange}
                            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                            placeholder="Header text"
                          />
                        </div>
                      </div>
                      <div className="border-b border-white/10 pb-3">
                        <p className="text-sm font-semibold text-white mb-2">Afternoon notification</p>
                        <div className="space-y-2">
                          <input
                            type="text"
                            name="afternoon_push_title"
                            value={editForm.afternoon_push_title}
                            onChange={handleEditInputChange}
                            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                            placeholder="Title (e.g. Afternoon Task)"
                          />
                          <input
                            type="text"
                            name="afternoon_push_header"
                            value={editForm.afternoon_push_header}
                            onChange={handleEditInputChange}
                            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                            placeholder="Header text"
                          />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white mb-2">Evening notification</p>
                        <div className="space-y-2">
                          <input
                            type="text"
                            name="evening_push_title"
                            value={editForm.evening_push_title}
                            onChange={handleEditInputChange}
                            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                            placeholder="Title (e.g. Evening Task)"
                          />
                          <input
                            type="text"
                            name="evening_push_header"
                            value={editForm.evening_push_header}
                            onChange={handleEditInputChange}
                            className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                            placeholder="Header text"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {editType === "level" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        XP Required
                      </label>
                      <input
                        type="number"
                        name="xp_required"
                        value={editForm.xp_required}
                        onChange={handleEditInputChange}
                        min="0"
                        className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                        placeholder="Enter XP required"
                      />
                    </div>
                  )}

                  {editType === "achievement" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Threshold
                      </label>
                      <input
                        type="number"
                        name="threshold"
                        value={editForm.threshold}
                        onChange={handleEditInputChange}
                        min="0"
                        className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary"
                        placeholder="Enter threshold"
                      />
                    </div>
                  )}

                  {editType !== "daily_tasks" && editType !== "how_to_earn_xp" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={editForm.description}
                      onChange={handleEditInputChange}
                      className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-orangePrimary min-h-[80px] resize-y"
                      placeholder="Enter description"
                    />
                  </div>
                  )}

                  {(editType === "level" || editType === "feature" || editType === "achievement") && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Badge Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      id="badge_image_upload"
                      style={{ display: "none" }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setImageUploading(true);
                        setEditError("");
                        try {
                          const formData = new FormData();
                          formData.append("files", file);
                          const response = await EventService.fileUpload(formData);
                          // Handle different possible response structures
                          const url = response?.data?.files?.[0] || response?.data?.data?.files?.[0] || response?.data?.url || response?.data;
                          if (url && typeof url === 'string') {
                            setEditForm((prev) => ({
                              ...prev,
                              badge_url: url,
                            }));
                          } else {
                            console.error("Unexpected upload response structure:", response);
                            setEditError("Badge image upload failed: Invalid response format.");
                          }
                        } catch (err) {
                          setEditError("Image upload failed. Please try again.");
                        } finally {
                          setImageUploading(false);
                        }
                      }}
                    />
                    {imageUploading ? (
                      <div className="flex min-h-11 w-full items-center justify-center rounded-xl border border-dashed border-white/10 mt-2 bg-black/40">
                        <img src={Loader} alt="Loading..." className="mx-auto h-10 w-10" />
                      </div>
                    ) : editForm.badge_url ? (
                      <label
                        htmlFor="badge_image_upload"
                        className="mb-2 mt-1 h-full w-full cursor-pointer block"
                      >
                        <img
                          src={editForm.badge_url}
                          alt="Badge"
                          className="h-32 w-full rounded-xl object-contain bg-black/40 border border-white/10"
                        />
                      </label>
                    ) : (
                      <label
                        htmlFor="badge_image_upload"
                        className="flex h-full min-h-11 w-full cursor-pointer items-center justify-center rounded-xl border border-dashed border-white/10 text-white duration-300 hover:bg-white/10 bg-black/40 text-sm"
                      >
                        Choose Image
                      </label>
                    )}
                  </div>
                  )}

                  {editError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 text-sm font-medium">{editError}</p>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 px-6 py-5 bg-navy-800/50">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setEditModalOpen(false);
                    setSelectedBadge(null);
                    setEditForm({ title: "", description: "", xp_required: "", badge_url: "", is_active: true, threshold: "", prompt_to_achieve: "", xp_reward: "", icon: "", action: "", morning_push_title: "", morning_push_header: "", afternoon_push_title: "", afternoon_push_header: "", evening_push_title: "", evening_push_header: "" });
                    setEditError("");
                  }}
                  className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdateBadge}
                  disabled={editLoading}
                  className="px-6 py-2.5 rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] hover:bg-[#369FFF1A]/20 transition-all duration-200 font-semibold text-sm border border-[#369FFF]/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[140px]"
                >
                  {editLoading ? (
                    <img src={Loader} alt="Loading..." className="h-5 w-5" />
                  ) : (
                    "Update"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {popupOpenDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <div
            className="w-96 rounded-xl bg-navy-800 p-6 shadow-lg"
            ref={deleteRef}
          >
            <h2 className="mb-4 text-xl font-bold text-white">Delete Badge</h2>
            <p className="mb-6 text-gray-300">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-white">
                {badgeToDelete?.title || badgeToDelete?.name || "this item"}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setPopupOpenDelete(false);
                  setBadgeToDelete(null);
                  document.body.style.overflow = "unset";
                }}
                className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteBadge}
                className="w-24 rounded-md bg-orangePrimary px-4 py-2 text-white hover:opacity-90 disabled:opacity-50"
                disabled={confirmLoader}
              >
                {confirmLoader ? (
                  <img
                    src={Loader}
                    alt="Loading..."
                    className="mx-auto h-6 w-6"
                  />
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gamification;

